import { Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";

export default function SessionsTab({ ctx }) {
  const {
    divisions, setEditingSession, refreshSessions, loading, archiveSession,
    assignSession, activeSessions, unassignedMembers, sessionMembersFor, divSessionsFor,
  } = ctx;
  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Sessions</h1>
        <div className="flex items-center gap-2">
          <button data-testid="add-session-btn" onClick={() => setEditingSession({ division_id: divisions.filter((d) => d.status === "active")[0]?.id || "", name: "", day_of_week: "", time: "", capacity: 8, sort_order: 0, status: "active" })}
            className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#2563EB] hover:bg-[#1d4fd7] rounded-full px-5 py-2.5 transition-colors">
            <Plus size={16} /> Add Session
          </button>
          <button data-testid="sessions-refresh-btn" onClick={refreshSessions} disabled={loading}
            className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-4 py-2.5 transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium text-white/50">Recurring weekly league nights. Each session belongs to a division and has its own capacity. Assign members below.</p>

      {activeSessions.length === 0 && (
        <p className="mt-8 text-white/40 font-medium">No sessions yet. Add a session (e.g. "Tuesday Night") to start scheduling members.</p>
      )}

      <div className="mt-8 space-y-5">
        {activeSessions.map((s) => {
          const sessionMembers = sessionMembersFor(s.id);
          const pct = s.capacity ? Math.min(100, Math.round((s.member_count / s.capacity) * 100)) : 0;
          const divSessions = divSessionsFor(s.division_id);
          return (
            <div key={s.id} data-testid={`session-${s.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-extrabold text-white">{s.name}</p>
                    <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${s.full ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>{s.full ? "Full" : `${s.remaining} open`}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-white/50">
                    {s.division_name}{s.day_of_week ? ` · ${s.day_of_week}` : ""}{s.time ? ` · ${s.time}` : ""} · {s.member_count}/{s.capacity} members
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-session-${s.id}`} onClick={() => setEditingSession({ ...s })} className="text-xs font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-3 py-2 inline-flex items-center gap-1.5"><Pencil size={13} /> Edit</button>
                  <button data-testid={`archive-session-${s.id}`} onClick={() => archiveSession(s.id)} className="text-xs font-bold text-red-300 hover:text-red-200 border border-white/15 rounded-full px-3 py-2"><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="mt-4 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full rounded-full ${s.full ? "bg-red-400" : "bg-[#2563EB]"}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-4 space-y-2">
                {sessionMembers.length === 0 && <p className="text-sm text-white/40">No members assigned.</p>}
                {sessionMembers.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-2.5">
                    <p className="text-sm font-semibold text-white">{m.player_name} <span className="text-white/40 font-medium">· {m.division_name}</span></p>
                    <select data-testid={`session-move-${m.id}`} value={s.id} onChange={(e) => assignSession(m.id, e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-[#2563EB]">
                      {divSessions.map((ds) => <option key={ds.id} value={ds.id} className="bg-[#0B0F19]">Move to: {ds.name}</option>)}
                      <option value="" className="bg-[#0B0F19]">Unassign</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Unassigned members */}
      {unassignedMembers.length > 0 && (
        <div className="mt-8" data-testid="unassigned-members">
          <p className="text-sm font-bold uppercase tracking-wider text-white/40">Unassigned Members ({unassignedMembers.length})</p>
          <div className="mt-3 space-y-2">
            {unassignedMembers.map((m) => {
              const divSessions = divSessionsFor(m.division_id);
              return (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                  <p className="text-sm font-semibold text-white">{m.player_name} <span className="text-white/40 font-medium">· {m.division_name}</span></p>
                  {divSessions.length === 0 ? (
                    <span className="text-xs text-white/40">No session in this division</span>
                  ) : (
                    <select data-testid={`assign-session-${m.id}`} defaultValue="" onChange={(e) => e.target.value && assignSession(m.id, e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-[#2563EB]">
                      <option value="" className="bg-[#0B0F19]">Assign to session…</option>
                      {divSessions.map((ds) => <option key={ds.id} value={ds.id} disabled={ds.full} className="bg-[#0B0F19]">{ds.name}{ds.full ? " (Full)" : ` · ${ds.remaining} open`}</option>)}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
